import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { signedOutEntry } from "@/auth/auto-start";
import { enterReaderSession, type ReaderStart } from "@/auth/enter";
import { DesignSkeleton } from "@/components/design-switcher/skeleton";
import {
  needsReaderSelection,
  readerIdOf,
  type SessionState,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";
import { AUTO_START_PARAM, VIA_PARAM } from "@/contracts/session";
import { BookmarkIdentityGate } from "@/library/bookmark-identity-gate";
import { type DeviceReader, deviceReaders } from "@/storage/device-readers";
import { readNickname } from "@/storage/nickname";
import {
  type GateSource,
  unconfirmedSource,
} from "@/storage/unconfirmed-reader";
import { ReaderPickerDialog } from "./choose-reader";

export const SELECT_READER_PATH = "/select-reader";

// The Worker has already switched the session when a bookmark lands, so
// the gate guards every reader route, not just the page the scan opened:
// a reload, a new tab or a typed /reader/<id> must ask too. The session
// store writes the marker from the landing on the first answer, before any
// listener renders, so the gate is on screen as soon as the reader is. A
// device marker gates only a reader_session: its "Not me" starts a fresh
// reader, which is no answer for an account that holds this reader.
function useUnconfirmedReader(session: SessionState): {
  source: GateSource | null;
  confirm: () => void;
} {
  const readerId = readerIdOf(session);
  const lane = session.status === "ready" ? session.session.lane : null;
  const [confirmedReader, setConfirmedReader] = useState<string | null>(null);
  const marked =
    readerId === null || confirmedReader === readerId
      ? null
      : unconfirmedSource(readerId);
  const source =
    marked === "device" && lane !== "reader_session" ? null : marked;
  return { source, confirm: () => setConfirmedReader(readerId) };
}

// Landings once named the gated reader as `gate=<readerId>`; a URL from
// history still can, and the id must leave the address bar.
const LEGACY_GATE_PARAM = "gate";
const LANDING_PARAMS = [AUTO_START_PARAM, VIA_PARAM, LEGACY_GATE_PARAM];

// A landing has done its work once a session answers: the store has
// settled it, and the URL must neither decline a later attempt from a
// reload nor gate again on Back. Until then it stays, so a first read that
// fails lets a reload settle it.
function useLandedEntry(ready: boolean): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const landed = ready && LANDING_PARAMS.some((name) => searchParams.has(name));
  useEffect(() => {
    if (!landed) return;
    setSearchParams(
      (params) => {
        for (const name of LANDING_PARAMS) params.delete(name);
        return params;
      },
      { replace: true },
    );
  }, [landed, setSearchParams]);
}

// A visitor needs no account to read: a signed-out session is sent through
// the Worker's reader entry, which signs in a reader and lands back here,
// or, when the device remembers several readers, asked which one it is.
// A full page load, because the entry is a Worker route, not the SPA's. The
// decision holds for the mount, so a query change before the page unloads
// (the bookmark landing's own cleanup) cannot flash the signed-out route.
function SignedOut({ navigate }: { navigate: (start: ReaderStart) => void }) {
  const location = useLocation();
  const [entry] = useState(() => signedOutEntry(location));
  // StrictMode replays effects; one mount is one round trip.
  const started = useRef(false);
  useEffect(() => {
    if (entry.kind !== "start" || started.current) return;
    started.current = true;
    navigate(entry.start);
  }, [entry, navigate]);
  if (entry.kind === "declined") return <Outlet />;
  if (entry.kind === "choose") {
    return <ReaderPickerDialog readers={entry.readers} returnTo={location} />;
  }
  return <DesignSkeleton />;
}

// "Not me" on the device gate means someone else holds the device: when it
// remembers other readers they get to pick themselves, as a signed-out
// visitor would, rather than being handed a fresh reader. The picker stays
// modal, as the gate was: the chrome behind it still acts as the declined
// reader.
function IdentityGate({
  readerId,
  source,
  navigate,
  onConfirmed,
}: {
  readerId: string;
  source: GateSource;
  navigate: (start: ReaderStart) => void;
  onConfirmed: () => void;
}) {
  const location = useLocation();
  const [others, setOthers] = useState<DeviceReader[] | null>(null);
  if (others !== null) {
    return <ReaderPickerDialog readers={others} returnTo={location} />;
  }
  const startFresh = (start: ReaderStart) => {
    const otherReaders = deviceReaders().filter(
      (reader) => reader.readerId !== readerId,
    );
    if (otherReaders.length > 0) setOthers(otherReaders);
    else navigate(start);
  };
  return (
    <>
      <DesignSkeleton />
      <BookmarkIdentityGate
        readerId={readerId}
        source={source}
        nickname={readNickname(readerId)}
        startFresh={startFresh}
        onConfirmed={onConfirmed}
      />
    </>
  );
}

const enter = (start: ReaderStart) => enterReaderSession(start);

// An unreachable session, or a signed-out one the reader entry already
// failed, still renders the route: its queries answer null for no reader,
// and a reader on a dead connection keeps reading.
export function RequireReader({
  store = sessionStore,
  navigate = enter,
}: {
  store?: SessionStore;
  navigate?: (start: ReaderStart) => void;
}) {
  const session = useSession(store);
  const readerId = readerIdOf(session);
  const { source, confirm } = useUnconfirmedReader(session);
  useLandedEntry(session.status === "ready");
  if (session.status === "loading") return <DesignSkeleton />;
  if (session.status === "signed-out") return <SignedOut navigate={navigate} />;
  if (needsReaderSelection(session)) {
    return <Navigate to={SELECT_READER_PATH} replace />;
  }
  if (source !== null && readerId !== null) {
    return (
      <IdentityGate
        key={readerId}
        readerId={readerId}
        source={source}
        navigate={navigate}
        onConfirmed={confirm}
      />
    );
  }
  return <Outlet />;
}
